import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { ChevronUp, ChevronDown, Trash2, Plus, X, Edit2 } from 'lucide-react';

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    query: '',
    id: '',
    tags: [] // Local state for tags
  });
  const [currentTag, setCurrentTag] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const docRef = doc(db, 'content', 'main');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const fetchedCategories = docSnap.data().categories || [];
        // Split the query string into tags array for display
        const processedCategories = fetchedCategories.map(cat => ({
          ...cat,
          tags: cat.query.split(' AND ')
        }));
        setCategories(processedCategories);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching categories: ", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'id') {
      setNewCategory({ ...newCategory, [name]: value.toLowerCase() });
    } else {
      setNewCategory({ ...newCategory, [name]: value });
    }
  };

  const handleAddTag = () => {
    if (!currentTag.trim()) return;
    
    const lowercaseTag = currentTag.trim().toLowerCase();
    if (!newCategory.tags.includes(lowercaseTag)) {
      setNewCategory({
        ...newCategory,
        tags: [...newCategory.tags, lowercaseTag]
      });
    }
    setCurrentTag('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setNewCategory({
      ...newCategory,
      tags: newCategory.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleAddCategory = async () => {
    if (!newCategory.name || !newCategory.id || newCategory.tags.length === 0) {
      alert("Please fill in all fields and add at least one tag.");
      return;
    }

    try {
      const docRef = doc(db, 'content', 'main');
      const categoryToAdd = {
        name: newCategory.name,
        id: newCategory.id,
        query: newCategory.tags.join(' AND ') // Join tags with AND for database
      };

      if (editingCategory) {
        const updatedCategories = categories.map(cat => 
          cat.id === editingCategory.id ? categoryToAdd : cat
        );
        await updateDoc(docRef, { categories: updatedCategories });
      } else {
        await updateDoc(docRef, { 
          categories: arrayUnion(categoryToAdd) 
        });
      }

      resetForm();
      fetchCategories();
    } catch (error) {
      console.error("Error adding/editing category: ", error);
      alert("Error saving category.");
    }
  };

  const handleDeleteCategory = async (categoryToDelete) => {
    const confirmation = window.confirm("Are you sure you want to delete this category?");
    if (confirmation) {
      try {
        const docRef = doc(db, 'content', 'main');
        const updatedCategories = categories.filter(c => c.id !== categoryToDelete.id);
        await updateDoc(docRef, { categories: updatedCategories });
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category: ", error);
        alert("Error deleting category.");
      }
    }
  };

  const moveCategory = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= categories.length) return;
    
    try {
      const updatedCategories = [...categories];
      const [movedCategory] = updatedCategories.splice(fromIndex, 1);
      updatedCategories.splice(toIndex, 0, movedCategory);
      
      const docRef = doc(db, 'content', 'main');
      // Convert tags array back to query string before saving
      const categoriesToSave = updatedCategories.map(({ tags, ...rest }) => ({
        ...rest,
        query: Array.isArray(tags) ? tags.join(' AND ') : rest.query
      }));
      
      await updateDoc(docRef, { categories: categoriesToSave });
      setCategories(updatedCategories);
    } catch (error) {
      console.error("Error updating category order: ", error);
    }
  };

  const resetForm = () => {
    setNewCategory({
      name: '',
      query: '',
      id: '',
      tags: []
    });
    setCurrentTag('');
    setEditingCategory(null);
    setIsDropdownOpen(false);
  };

  if (loading) {
    return <div className="text-center p-8">Loading categories...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl font-bold mb-4 md:mb-0">Categories Manager</h1>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          {isDropdownOpen ? <X size={20} /> : <Plus size={20} />}
          {isDropdownOpen ? "Cancel" : "Add New Category"}
        </button>
      </div>

      {/* Add/Edit Category Form */}
      {isDropdownOpen && (
        <div className="bg-gray-800 p-6 rounded-lg mb-6 shadow-lg">
          <h3 className="text-xl font-bold mb-4">
            {editingCategory ? "Edit Category" : "Add New Category"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="name"
              value={newCategory.name}
              onChange={handleInputChange}
              placeholder="Category Name"
              className="w-full p-3 bg-gray-700 rounded-lg"
            />
            <input
              type="text"
              name="id"
              value={newCategory.id}
              onChange={handleInputChange}
              placeholder="Category ID (lowercase), Will be in a URL"
              className="w-full p-3 bg-gray-700 rounded-lg"
            />
            <div className="md:col-span-2">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  placeholder="Add a tag (press Enter or click Add)"
                  className="flex-1 p-3 bg-gray-700 rounded-lg"
                />
                <button
                  onClick={handleAddTag}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Add Tag
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {newCategory.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-700 text-sm px-2 py-1 rounded-full flex items-center gap-2"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-4 mt-6">
            <button
              onClick={handleAddCategory}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors"
            >
              {editingCategory ? "Save Changes" : "Add Category"}
            </button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-4">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className="bg-gray-800 rounded-lg p-4 shadow-lg transition-all hover:shadow-xl"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold">{category.name}</h3>
                <p className="text-gray-400 text-sm">ID: {category.id}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {category.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-gray-700 text-sm px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="flex gap-1">
                  <button
                    onClick={() => moveCategory(index, index - 1)}
                    className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <ChevronUp size={20} />
                  </button>
                  <button
                    onClick={() => moveCategory(index, index + 1)}
                    className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <ChevronDown size={20} />
                  </button>
                </div>
                <button
                  onClick={() => {
                    setEditingCategory(category);
                    setNewCategory({
                      ...category,
                      tags: category.tags // Use the split tags from our processed data
                    });
                    setIsDropdownOpen(true);
                  }}
                  className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
                >
                  <Edit2 size={20} />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category)}
                  className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoriesManager;